'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useId, useRef, useState } from 'react';

import {
  COOKIE_CATEGORIES,
  type CookieCategory,
} from '@eliadmin/core/src/components/dlz/cookies/cookie-categories';
import { COOKIES_COPY } from '@eliadmin/core/src/components/dlz/cookies/cookies-copy';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Switch } from '@eliadmin/core/src/components/ui/switch';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/**
 * Figma „Toggle“ (60320:19075): 44 × 24 track – bg/brand on, bg/muted off, bg/disabled when
 * disabled (always on) – with a 20 px white knob 2 px from the edge, shadow 0 1 2 / 15 %.
 */
const SWITCH = {
  wrapper:
    'h-6 w-11 shadow-none focus-visible:ring-[#0F1012] focus-visible:ring-offset-white disabled:opacity-100 data-[state=checked]:bg-[#D7191F] data-[state=unchecked]:bg-[#E2E4E7] disabled:data-[state=checked]:bg-[#ECEDEF]',
  thumb: 'h-5 w-5 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.15)] data-[state=checked]:translate-x-5',
};

type Choice = {
  /** Categories accepted so far (engine names); the switches start from them. */
  accepted: readonly CookieCategory[];
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSave: (categories: CookieCategory[]) => void;
};

type CookieSettingsDialogProps = Choice & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Figma „Cookie settings modal“ 60326:23645 (680 px card centred over a 45 % overlay) and
 * „Cookie settings modal · Mobile“ 60370:44684 (bottom sheet 44 px below the top with a handle, the
 * buttons stacked „Prijať všetky“ first). Header and footer stay, the categories scroll. The tablet
 * width (no Figma) uses the desktop card.
 */
export function CookieSettingsDialog({ open, onOpenChange, ...choice }: CookieSettingsDialogProps) {
  const { t } = useTranslations();
  // The dialog is opened from several places (bar, footer link, `?occ=true`) and has no Radix
  // trigger, so the element to return the focus to is remembered here.
  const opener = useRef<HTMLElement | null>(null);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-[#0F1012]/45 data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <Dialog.Content
          onOpenAutoFocus={() => {
            opener.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={event => {
            event.preventDefault();
            if (opener.current?.isConnected) opener.current.focus();
          }}
          className="fixed inset-x-0 bottom-0 top-11 z-[200] flex flex-col overflow-hidden rounded-t-[18px] bg-white shadow-dropdown focus:outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom motion-reduce:animate-none md:inset-auto md:left-1/2 md:top-1/2 md:max-h-[calc(100dvh-48px)] md:w-[calc(100%-48px)] md:max-w-[680px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[18px] md:data-[state=open]:fade-in-0 md:data-[state=open]:zoom-in-95 md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-1/2"
        >
          <span
            aria-hidden
            className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-sm bg-[#E2E4E7] md:hidden"
          />
          <header className="flex shrink-0 items-center gap-3 border-b border-[#E2E4E7] px-4 py-6 md:p-6">
            <Dialog.Title className="dlz-h3 min-w-0 flex-1 text-[#0F1012]">
              {COOKIES_COPY.settings.title}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t('text272')}
              className="-m-2.5 shrink-0 rounded-full p-2.5 text-[#6B7079] transition-colors hover:text-[#0F1012] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
            >
              <Icon name="x" size={24} />
            </Dialog.Close>
          </header>
          <CookieChoice {...choice} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Body + footer; mounted with the open dialog, so every opening starts from the stored choice. */
function CookieChoice({ accepted, onAcceptAll, onRejectAll, onSave }: Choice) {
  const { t } = useTranslations();
  const [selected, setSelected] = useState<readonly CookieCategory[]>(accepted);

  const toggle = (category: CookieCategory, on: boolean) =>
    setSelected(current =>
      on ? [...current, category] : current.filter(value => value !== category),
    );

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 md:p-6">
        <Dialog.Description className="dlz-body-m text-[#4F545C]">
          {COOKIES_COPY.settings.intro}
        </Dialog.Description>
        <ul>
          {COOKIE_CATEGORIES.map(category => (
            <CategoryRow
              key={category.engine}
              {...COOKIES_COPY.categories[category.copy]}
              readOnly={category.readOnly}
              checked={category.readOnly || selected.includes(category.engine)}
              onCheckedChange={on => toggle(category.engine, on)}
            />
          ))}
        </ul>
      </div>
      <footer className="flex shrink-0 flex-col gap-2 border-t border-[#E2E4E7] px-4 py-6 md:flex-row md:gap-3 md:p-6">
        {/* DOM order = desktop order; phones lift „Prijať všetky“ to the top. */}
        <Button
          type="button"
          variant="secondary"
          onClick={onRejectAll}
          className="h-12 w-full md:mr-auto md:h-[52px] md:w-auto"
        >
          {COOKIES_COPY.rejectAll}
        </Button>
        <Button
          type="button"
          variant="success"
          onClick={() => onSave([...selected])}
          className="h-12 w-full md:h-[52px] md:w-auto"
        >
          {COOKIES_COPY.settings.save}
        </Button>
        <Button
          type="button"
          onClick={onAcceptAll}
          className="h-12 w-full max-md:order-first md:h-[52px] md:w-auto"
        >
          {t('text2079')}
        </Button>
      </footer>
    </>
  );
}

type CategoryRowProps = {
  title: string;
  description: string;
  readOnly: boolean;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

/** Figma „Category · …“: 20 px vertical padding, a line between rows, the switch on the right. */
function CategoryRow({ title, description, readOnly, checked, onCheckedChange }: CategoryRowProps) {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <li className="flex items-center gap-4 border-b border-[#E2E4E7] py-5 last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col gap-1 md:gap-2">
        <p id={titleId} className="dlz-body-m-medium text-[#0F1012]">
          {title}
        </p>
        <p id={descriptionId} className="dlz-body-s text-[#6B7079]">
          {description}
        </p>
      </div>
      <Switch
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        checked={checked}
        disabled={readOnly}
        onCheckedChange={onCheckedChange}
        classNames={SWITCH}
      />
    </li>
  );
}
